import React, { useState, isValidElement } from 'react';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useRole,
  useInteractions,
  FloatingPortal,
  Placement,
} from '@floating-ui/react';
import clsx from 'clsx';

import { tooltip } from './Tooltip.css';
import { Colors, sprinkles, typography } from '../../tokens';
import { Flex } from '../Flex';

export interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode;
  shortcut?: string;
  placement?: 'top' | 'right';
  disabled?: boolean;
  className?: string;
}

export const Tooltip = ({
  children,
  content,
  shortcut,
  placement = 'top',
  disabled = false,
  className,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // placement에 따른 fallback 순서 정의
  const getFallbackPlacements = (placement: Placement) => {
    const fallbacks: Record<string, Placement[]> = {
      top: ['top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end'],
      bottom: ['bottom-start', 'bottom-end', 'top', 'top-start', 'top-end'],
      left: ['left-start', 'left-end', 'right', 'right-start', 'right-end'],
      right: ['right-start', 'right-end', 'left', 'left-start', 'left-end'],
    };
    return fallbacks[placement] || undefined;
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: getFallbackPlacements(placement),
        fallbackAxisSideDirection: 'start',
      }),
      shift({
        padding: 8,
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: !disabled,
  });

  const focus = useFocus(context, {
    enabled: !disabled,
  });

  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role]);

  if (!isValidElement(children)) {
    return children;
  }

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className={clsx(sprinkles({ display: 'inline-flex' }), className)}
      >
        {children}
      </span>
      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={tooltip}
          >
            <Flex align="center" justify="center" gap={8}>
              {typeof content === 'string' ? (
                <span className={typography['13_400']} style={{ color: Colors.white }}>
                  {content}
                </span>
              ) : (
                content
              )}
              {shortcut && (
                <span className={typography['13_400']} style={{ color: Colors.gray400 }}>
                  {shortcut}
                </span>
              )}
            </Flex>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
